// src/features/messages/components/AudienceLine.jsx
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Groups as GroupsIcon, Badge as RoleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../components/common/ProfilePicture';
import { AVATAR_CHIP_SX } from '../constants';

/**
 * Who a message was addressed to.
 * `compact` is one line of text for lists; otherwise chips.
 */
export default function AudienceLine({ audience, compact = false }) {
  const { t } = useTranslation(['messages']);
  if (!audience) return null;

  if (compact) {
    const names = audience.all
      ? [t('messages:audience.all')]
      : [...audience.roles.map(r => r.name), ...audience.staff.map(s => s.name)];
    return (
      <Typography variant="caption" color="text.secondary" noWrap component="div">
        {t('messages:audience.to')}: {names.join(', ')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        {t('messages:audience.to')}:
      </Typography>
      {audience.all ? (
        <Chip size="small" variant="outlined" icon={<GroupsIcon />} label={t('messages:audience.all')} />
      ) : (
        <>
          {audience.roles.map(role => (
            <Chip key={role.role_id} size="small" variant="outlined" icon={<RoleIcon />} label={role.name} />
          ))}
          {audience.staff.map(staff => (
            <Chip
              key={staff.staff_member_id}
              size="small"
              variant="outlined"
              sx={AVATAR_CHIP_SX}
              avatar={(
                <ProfilePicture
                  profilePictureFileId={staff.profile_picture_file_id}
                  name={staff.name}
                  color={staff.color}
                  size={20}
                  imageSize="32x32"
                />
              )}
              label={staff.name}
            />
          ))}
        </>
      )}
    </Box>
  );
}
